import React from 'react';
import styled, { x } from '@xstyled/styled-components';
import { t } from 'ttag';
import { languageName } from '~/lib/translations/languageName';
import { useLightSwitchesAudioEffect } from '~/ui/audio-effect/useLightSwitchesAudioEffect';
import { CloseButton } from '~/ui/buttons/CloseButton';
import { Flex } from '~/ui/flex/Flex';
import { PickList } from '~/ui/pick-list/PickList';
import { Legend } from '../Header';
import { SettingsPages, useSettingsStore } from '../Store';
import { useTrainingStore } from '~/training/state';
import { getSupportedLanguageLabel } from '~/lib/translations/getSupportedLanguageLabel';

const ScrollContainer = styled.div`
  max-height: 60vh;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 8px;
  -webkit-overflow-scrolling: touch;
`;

const CenterTitle = styled.div`
  legend {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: -3px 0 30px 0;
  }
`;

export interface LocalePageProps {
  locale: string;
  availableLocales: string[];
  setLocale: (locale: string) => void;
  onClose?: () => void;
  centerTitle?: boolean;
  selectedLocale?: string;
}

export const LocalePage = ({
  locale,
  availableLocales,
  setLocale,
  onClose,
  centerTitle,
  selectedLocale
}: LocalePageProps) => {
  const { setPage } = useSettingsStore();
  const { supportedLanguages } = useTrainingStore();
  const [playAudioEffect] = useLightSwitchesAudioEffect();

  const languageItems = availableLocales.map(l => {
    const lng1 = languageName(l, selectedLocale || locale);
    const lng2 = languageName(l);

    return {
      locale: l,
      label: lng1 !== lng2 ? `${lng1} | ${lng2}` : `${lng1}`,
      additionalLabel: getSupportedLanguageLabel(l, supportedLanguages[l])
    };
  });

  return (
    <>
      <CloseButton
        onClick={() => {
          setPage(SettingsPages.Root);
          if (onClose) {
            onClose();
          }
        }}
      />
      <Flex variant="list">
        <x.fieldset p={0} m={0}>
          {centerTitle ? (
            <CenterTitle>
              <Legend>{t`Language`}</Legend>
            </CenterTitle>
          ) : (
            <Legend>{t`Language`}</Legend>
          )}
          <ScrollContainer>
            <PickList
              items={languageItems}
              selected={languageItems.find(l =>
                selectedLocale
                  ? l.locale === selectedLocale
                  : l.locale === locale
              )}
              valueField="locale"
              labelField="label"
              additionalField="additionalLabel"
              useSubtitle={!!selectedLocale}
              onSelect={item => {
                document.documentElement.setAttribute('lang', item.locale);
                try {
                  playAudioEffect();
                } catch (error) {
                  // Silently ignore audio effect errors
                }
                setLocale(item.locale);
                setPage(SettingsPages.Root);

                if (onClose) {
                  onClose();
                }
              }}
            />
          </ScrollContainer>
        </x.fieldset>
      </Flex>
    </>
  );
};
